import {
  Injectable,
  ComponentFactoryResolver,
  ComponentRef,
  ApplicationRef,
  Injector,
  inject,
} from '@angular/core';
import { Control, Map } from 'leaflet';
import { LegendComponent } from '../../components/molecules/legend/legend.component';
import { Scale } from '../../models/scale';
import { Indicator } from '../../models/indicator';
import { DataLayerTypeIcon } from '../../stores/data-layer-type/data-layer-type.selectors';
import { WegweiserIndicator } from '../grid-widget-config.service';

export interface LeafletLegend {
  legendControl: Control;
  legendComponentRef: ComponentRef<LegendComponent>;
}

export interface IndicatorLegend extends LeafletLegend {
  scales?: Scale[];
  icons: DataLayerTypeIcon[];
}

@Injectable({
  providedIn: 'root',
})
export class MapLegendService {
  private readonly resolver = inject(ComponentFactoryResolver);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(Injector);

  /**
   * Adds a legend component to the specified map with the provided configuration options.
   *
   * @param {Map} map - The map instance to which the legend component will be added.
   * @param {Scale[]} scales - An array of scales representing the data ranges for the legend.
   * @param {string[]} colors - An array of color strings corresponding to the scales.
   * @param {Indicator | WegweiserIndicator} indicator - The indicator object associated with the legend.
   * @param {DataLayerTypeIcon[]} icons - An array of icons that visually represent data layers.
   * @param {'topleft' | 'topright' | 'bottomleft' | 'bottomright'} [position='bottomright'] - The position of the legend on the map.
   * @param {'small' | 'large'} [size='large'] - The size of the legend component.
   * @param {string[]} [displayedComparisonValues] - An optional array of comparison values to display in the legend.
   * @param {{ [key: string]: number }} [comparisonData] - An optional object containing comparison data for additional legend configuration.
   * @return {LeafletLegend} An object containing the Leaflet control and the legend component reference.
   */
  addLegendComponent(
    map: Map,
    scales: Scale[],
    colors: string[],
    indicator: Indicator | WegweiserIndicator,
    icons: DataLayerTypeIcon[],
    position:
      | 'topleft'
      | 'topright'
      | 'bottomleft'
      | 'bottomright' = 'bottomright',
    size: 'small' | 'large' = 'large',
    displayedComparisonValues?: string[],
    comparisonData?: { [key: string]: number }
  ): LeafletLegend {
    const factory = this.resolver.resolveComponentFactory(LegendComponent);
    const legendComponentRef = factory.create(this.injector);

    legendComponentRef.instance.scales = scales;
    legendComponentRef.instance.colors = colors;
    legendComponentRef.instance.indicator = indicator;
    legendComponentRef.instance.icons = icons;
    legendComponentRef.instance.size = size;
    legendComponentRef.instance.displayedComparisonValues =
      displayedComparisonValues;

    if (comparisonData) {
      legendComponentRef.instance.comparisonData = comparisonData;
    }

    this.appRef.attachView(legendComponentRef.hostView);
    const domElem = (legendComponentRef.hostView as any)
      .rootNodes[0] as HTMLElement;

    const legendControl = new Control({ position });

    legendControl.onAdd = () => {
      return domElem;
    };

    legendControl.addTo(map);
    return { legendControl, legendComponentRef };
  }

  /**
   * Adds a simple indicator legend to a Leaflet map.
   *
   * @param {Map} map The Leaflet map instance to which the legend will be added.
   * @param {Indicator[]} indicators An array of indicators to display in the legend.
   * @param {string[]} colors An array of colors corresponding to the indicators.
   * @param {DataLayerTypeIcon} icon The icon to represent the data layers in the legend.
   * @param {'topleft' | 'topright' | 'bottomleft' | 'bottomright'} [position='bottomright'] The position of the legend on the map.
   * @return {LeafletLegend} An object containing the legend control and its component reference.
   */
  addSimpleIndicatorLegend(
    map: Map,
    indicators: Indicator[],
    colors: string[],
    icon: DataLayerTypeIcon,
    position:
      | 'topleft'
      | 'topright'
      | 'bottomleft'
      | 'bottomright' = 'bottomright'
  ): LeafletLegend {
    const legendComponentRef = this.resolver
      .resolveComponentFactory(LegendComponent)
      .create(this.injector);

    legendComponentRef.instance.type = 'indicator';
    legendComponentRef.instance.indicators = indicators;
    legendComponentRef.instance.indicatorColors = colors;
    legendComponentRef.instance.icons = [icon];

    this.appRef.attachView(legendComponentRef.hostView);

    const legendControl = new Control({ position });
    legendControl.onAdd = () => {
      const div = (legendComponentRef.hostView as any)
        .rootNodes[0] as HTMLElement;
      return div;
    };

    legendControl.addTo(map);

    return { legendControl, legendComponentRef };
  }

  /**
   * Removes a legend from the specified map and cleans up its associated resources.
   *
   * @param {Map} map - The map instance from which the legend will be removed.
   * @param {LeafletLegend} legend - The legend object to be removed, containing its component reference and control.
   * @return {void} No return value.
   */
  removeLegend(map: Map, legend: LeafletLegend): void {
    this.appRef.detachView(legend.legendComponentRef.hostView);
    legend.legendComponentRef.destroy();
    map.removeControl(legend.legendControl);
  }
}
